import { Component, OnInit, ViewChild } from '@angular/core';
import { RepositoryService } from '../../services/repository/repository.service';
import ArticleCategory from '../../../models/ArticleCategory';
import { ActivatedRoute, Router } from '@angular/router';
import { ToOrderButtonComponent } from '../../components/to-order-button/to-order-button.component';
import { AlertController, IonSearchbar, ModalController } from '@ionic/angular';
import { ModalInfoComponent } from '../../components/modal-info/modal-info.component';
import Article from '../../../models/Article';
import Utils from '../../../utils';
import ArticleGroup from '../../../models/ArticleGroup';
import { OrderListAction } from '../../enums/OrderListAction';
import { AppComponent } from '../../app.component';
import { PreorderType } from '../../../enums/PreorderType';
import * as moment from 'moment';
import { CheckoutModalComponent } from '../../components/checkout-modal/checkout-modal.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrderPage } from '../order/order.page';
import { HomePage } from '../home/home.page';
import RepositoryInterface from '../repository-interface';
import { TranslateService } from '@ngx-translate/core';
import { OrderUtils } from '../../../utils/order-utils';
import { OrderType } from '../../../enums/OrderType';
import { modalEnterAnimation } from '../../../animations';
import { AngularFireAnalytics } from '@angular/fire/analytics';

@Component({
	selector: 'app-menu',
	templateUrl: '../../../projects/mymonza/pages/menu/menu.page.html',
	styleUrls: ['../../../projects/mymonza/pages/menu/menu.page.scss'],
})
export class MenuPage extends RepositoryInterface implements OnInit {
	static url = 'menu';

	@ViewChild(ToOrderButtonComponent, { static: false }) toOrderButton;
	@ViewChild('categoryList', { static: false }) categoryList;
	@ViewChild('articleContent', { static: false }) articleList;
	@ViewChild(IonSearchbar, { static: false }) searchBar;

	selectedCategory: ArticleCategory = null;
	scrollEvents = true;
	loading = false;
	searchTerm = '';
	categories: ArticleCategory[] = [];
	moment = moment;
	isValid = false;
	utils = Utils;
	orderUtils = OrderUtils;
	pt = PreorderType;
	differenceToMvo: number;
	attempt = 1;

	constructor(
		private translate: TranslateService,
		protected repository: RepositoryService,
		private modalCtrl: ModalController,
		private snackbarCtrl: MatSnackBar,
		private route: ActivatedRoute,
		private alertCtrl: AlertController,
		private router: Router,
		private analytics: AngularFireAnalytics
	) {
		super(repository);
		if (this.venue) {
			this.reloadCategories();
			this.selectedCategory = this.categories[0];
		} else if (!repository.getStoredVenueId() || repository.getStoredVenueId() === null) {
			Utils.sleep(200).then(() => {
				snackbarCtrl
					.open(this.translate.instant('menu_page.venue_timed_out'), this.translate.instant('menu_page.venue_timed_out_action'))
					.onAction()
					.subscribe(() => {
						HomePage.navigate(router, repository);
					});
				this.loading = false;
			});
		}
		this.validate();
	}

	get active(): boolean {
		return this.order && OrderUtils.articleGroupsTotalPrice(
			this.order.orderedArticles,
			this.order.type,
			this.order.preorder.type,
			this.order.terminalorder ? this.order.terminalorder.type : null,
		) > 0;
	}

	get index(): number {
		return this.categories.findIndex(category => category._id === this.selectedCategory._id);
	}

	static async navigate(router: Router) {
		await router.navigateByUrl(MenuPage.url);
	}

	ngOnInit(): void {
		super.ngOnInit();
		switch (window.location.pathname) {
			case '/' + OrderPage.url:
				break;
			case '/' + OrderPage.paymentCancelUrl:
				console.log(this.translate.instant('order.payment_cancel'));
				this.snackbarCtrl.open(this.translate.instant('order.payment_cancel'), null, {
					duration: 2000,
				});
				break;
			case '/' + OrderPage.paymentFailUrl:
				console.log(this.translate.instant('order.payment_fail'));
				this.snackbarCtrl.open(this.translate.instant('order.payment_fail'), null, {
					duration: 5000,
				});
				break;
		}
	}

	largeScreen(): boolean {
		return AppComponent.largeScreen;
	}

	async scrollTo(index: number) {
		if (this.categories.length - 1 < index || index < 0) {
			return;
		}
		if (this.searchTerm !== '') {
			const inputElement = await this.searchBar.getInputElement();
			inputElement.value = '';
			this.searchTerm = '';
			this.reloadCategories();
			await this.delay(100);
			await this.scrollTo(index);
			return;
		}
		this.scrollEvents = false;
		this.selectedCategory = this.categories[index];
		await this.scrollArticleListTo(index);
		await this.scrollCategoryListTo(index);
		this.scrollEvents = true;
	}

	async onScroll(event) {
		let categoryElements: HTMLElement[];
		if (this.articleList.el.children[0].className === 'items-wrap') {
			// All other browsers
			categoryElements = [...this.articleList.el.children];
		} else {
			// IE/Edge
			categoryElements = [...this.articleList.el.children[1].children];
		}
		const categoryIndex = categoryElements
			.map(el => el.offsetTop)
			.map((value, index, array) => {
				return (
					value <= event.detail.currentY &&
					((index < array.length - 1 && event.detail.currentY < array[index + 1]) || index === array.length - 1)
				);
			})
			.findIndex(value => value);
		let prevIndex = -1;
		if (this.selectedCategory) {
			prevIndex = this.categories.findIndex(category => category._id === this.selectedCategory._id);
		}
		if (prevIndex === categoryIndex) {
			return;
		}
		if (this.scrollEvents) {
			this.selectedCategory = this.categories[categoryIndex];
			await this.scrollCategoryListTo(categoryIndex);
		}
	}

	private async scrollCategoryListTo(index: number) {
		console.log('scrollCategoryListTo(' + index + ')');
		if (
			index < 0 ||
			!this.categoryList ||
			!this.categoryList.nativeElement ||
			!this.categoryList.nativeElement.children[index] ||
			(!this.categoryList.nativeElement.scrollTo && !this.categoryList.nativeElement.scrollLeft)
		) {
			console.log('scrollCategoryListTo(' + index + ') cancelled');
			return;
		}
		if (this.categoryList.nativeElement.scrollTo) {
			// All other browsers
			this.categoryList.nativeElement.scrollTo(this.categoryList.nativeElement.children[index].offsetLeft - 25, 0);
		} else {
			// IE/Edge
			this.categoryList.nativeElement.scrollLeft = this.categoryList.nativeElement.children[index].offsetLeft - 25;
		}
		await this.delay(100);
	}

	private async scrollArticleListTo(index: number) {
		if (this.articleList.el.children[index].tagName !== 'MAIN') {
			// All other browsers
			await this.articleList.scrollToPoint(0, this.articleList.el.children[index].offsetTop, 300);
		} else {
			// IE/Edge
			await this.articleList.scrollToPoint(0, this.articleList.el.children[1].children[index].offsetTop, 300);
		}
	}

	async openModal(item: Article) {
		if (this.loading) {
			return;
		}
		const articleGroup = new ArticleGroup();
		articleGroup.article = item;
		articleGroup.groups.push(...Utils.defaultsToArticleOption(item, item.defaults, this.order.preorder.type));
		articleGroup.quantity = 1;
		const modal = await this.modalCtrl.create({
			cssClass: AppComponent.largeScreen ? 'item-modal large-modal' : 'item-modal',
			component: ModalInfoComponent,
			componentProps: {
				articleGroup: JSON.parse(JSON.stringify(articleGroup)),
			},
			mode: 'ios',
			swipeToClose: !this.largeScreen(),
			presentingElement: await this.modalCtrl.getTop(),
			backdropDismiss: true,
		});
		await modal.present();
		const response = await modal.onDidDismiss();
		if (response.data && response.data.articleGroup) {
			OrderUtils.addToOrder(this.order, response.data.articleGroup, this.analytics);
			this.repository.order.emit(this.order);
		}
	}

	onSearchTermChanged(event) {
		this.searchTerm = event.detail.value;
		this.reloadCategories();
	}

	reloadCategories() {
		if (!this.venue || !this.order) {
			this.categories = [];
			return;
		}
		const lowerSearchTerm = this.searchTerm && this.searchTerm !== '' ? this.searchTerm.toLocaleLowerCase() : null;
		this.categories = this.venue.articleCategories
			.filter(category => !category.hidden)
			.map(category => {
				const cat: ArticleCategory = JSON.parse(JSON.stringify(category));
				cat.articles = cat.articles.filter(article => {
					const available = Utils.getAvailability(article, OrderType.PREORDER, this.order.preorder.type);
					if (lowerSearchTerm) {
						const keys = [
							article.name.de.toLocaleLowerCase(),
							article.name.en.toLocaleLowerCase(),
							cat.name.de.toLocaleLowerCase(),
							cat.name.en.toLocaleLowerCase(),
						];
						return available &&
							keys.map(key => key.indexOf(lowerSearchTerm)).find(result => result >= 0) !== undefined;
					}
					return available;
				});
				return cat;
			})
			.filter(category => category.articles.length > 0);
		return;
	}

	async openCheckoutModal() {
		if (!this.isValid) {
			return;
		}
		this.loading = true;
		const modal = await this.modalCtrl.create({
			component: CheckoutModalComponent,
			showBackdrop: true,
			cssClass: 'checkout-modal-full',
			backdropDismiss: true,
		});
		await modal.present();
		const result = await modal.onDidDismiss();
		if (result.data === undefined) {
			this.loading = false;
			return;
		}
		this.order.orderAt = result.data.orderAt;
		this.order.preorder = result.data.preorder;
		this.repository.order.emit(this.order);
		await Utils.sendOrder(
			this.router,
			this.modalCtrl,
			this.translate,
			this.repository,
			this.order,
			loading => (this.loading = loading),
			this.snackbarCtrl,
			this.analytics
		);
	}


	validate() {
		if (!this.order) {
			return;
		}
		if (!this.venue || !this.order.preorder) {
			this.isValid = false;
			return;
		}
		const mov = this.order.preorder.type === PreorderType.DELIVERY ? +this.venue.movDelivery.$numberDecimal : 0;
		const orderValue = OrderUtils.articleGroupsTotalPrice(
			this.order.orderedArticles,
			this.order.type,
			this.order.preorder.type,
			this.order.terminalorder ? this.order.terminalorder.type : null,
		);
		this.differenceToMvo = orderValue - mov;
		this.isValid = this.differenceToMvo >= 0;
	}

	onVenue() {
		super.onVenue();
		this.reloadCategories();
		this.selectedCategory = this.venue && this.categories.length > 0 ? this.categories[0] : null;
		this.validate();
	}

	onVenueError(error) {
		super.onVenueError(error);
		this.snackbarCtrl.open(Utils.axiosErrorToMessage(this.translate, error), null, {
			duration: 2000,
		});
		this.attempt++;
		if (this.attempt > 5) {
			this.loading = false;
			this.snackbarCtrl.open(
				this.translate.instant('menu_page.venue_timed_out'),
				this.translate.instant('menu_page.venue_timed_out_action'),
			);
		} else {
			this.repository.getVenue();
		}
	}

	onOrder() {
		super.onOrder();
		this.validate();
	}

	async delay(ms: number) {
		return new Promise(resolve => setTimeout(resolve, ms));
	}
}
